(function () {
  'use strict';

  const U = (window.Universe = window.Universe || {});

  /**
   * UgcModal
   *
   * Opens a vertical (TikTok/reel style) modal from a UGC carousel slide when its
   * expand button (.js-ugc-expand) is clicked. Each slide ships its modal markup
   * inside a sibling <template class="js-ugc-modal-tpl">, so the video and any
   * product form stay inert until the template is cloned into the shell on open.
   *
   * A single instance handles every carousel on the page via a delegated click
   * listener, so it survives Shopify section re-renders and only one modal is
   * ever open at a time.
   */
  class UgcModal {
    constructor() {
      this.isOpen = false;
      this.forms = [];

      this.onExpandClick = this.onExpandClick.bind(this);
      this.onEscape = this.onEscape.bind(this);
      this.onTabTrap = this.onTabTrap.bind(this);
      this.onOverlayClick = this.onOverlayClick.bind(this);
      this.close = this.close.bind(this);
      this.handleCartUpdate = this.handleCartUpdate.bind(this);

      document.addEventListener('click', this.onExpandClick);
    }

    onExpandClick(e) {
      const btn = e.target.closest('.js-ugc-expand');
      if (!btn) return;

      // Stop the surrounding card link (if any) from navigating.
      e.preventDefault();
      e.stopPropagation();

      if (this.isOpen) return;

      const slide = btn.closest('.js-ugc-carousel-slide') || btn.parentElement;
      const tpl = slide && slide.querySelector('.js-ugc-modal-tpl');
      if (!tpl || !('content' in tpl)) return;

      this.open(tpl, btn);
    }

    open(tpl, opener) {
      this.opener = opener;
      this.closeLabel = opener.dataset.closeLabel || 'Close';

      // Stop the carousel card's own video from playing behind the modal;
      // remember which were playing so we can resume them on close.
      const slide = opener.closest('.js-ugc-carousel-slide');
      this.pausedVideos = slide ? Array.from(slide.querySelectorAll('video')).filter((v) => !v.paused) : [];
      this.pausedVideos.forEach((v) => v.pause());

      this.buildShell();
      this.DOM.content.appendChild(tpl.content.cloneNode(true));
      document.body.appendChild(this.DOM.overlay);
      document.body.classList.add('modal-open');

      this.wireForms();
      this.setBackgroundInert(true);

      this.DOM.overlay.addEventListener('click', this.onOverlayClick);
      this.DOM.closeBtn.addEventListener('click', this.close);
      document.addEventListener('keydown', this.onEscape);
      document.addEventListener('keydown', this.onTabTrap);

      const { subEvents, PUB_SUB_EVENTS } = U;
      if (subEvents && PUB_SUB_EVENTS) {
        this.cartUpdateUnsub = subEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
      }

      // Force reflow so the open transition runs from the initial state.
      requestAnimationFrame(() => this.DOM.overlay.classList.add('ugc-modal--open'));
      this.isOpen = true;

      this.playVideos();
      this.focusTrapActive = true;
      setTimeout(() => this.focusFirst(), UgcModal.ANIM);
    }

    buildShell() {
      const overlay = document.createElement('div');
      overlay.className = 'ugc-modal js-ugc-modal';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.innerHTML =
        '<div class="ugc-modal__dialog">' +
        '<button type="button" class="ugc-modal__close js-ugc-modal-close" aria-label="' +
        this.closeLabel.replace(/"/g, '&quot;') +
        '">' +
        '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>' +
        '</button>' +
        '<div class="ugc-modal__content js-ugc-modal-content"></div>' +
        '</div>';

      this.DOM = {};
      this.DOM.overlay = overlay;
      this.DOM.dialog = overlay.querySelector('.ugc-modal__dialog');
      this.DOM.content = overlay.querySelector('.js-ugc-modal-content');
      this.DOM.closeBtn = overlay.querySelector('.js-ugc-modal-close');
    }

    playVideos() {
      this.DOM.content.querySelectorAll('video').forEach((video) => {
        video.play().catch(() => {});
      });
    }

    wireForms() {
      const { ProductForm } = U;
      if (!ProductForm) return;
      this.DOM.content.querySelectorAll('.js-product-form').forEach((form) => {
        this.forms.push(new ProductForm(form));
      });
    }

    destroyForms() {
      this.forms.forEach((form) => form.destroy());
      this.forms = [];
    }

    handleCartUpdate(data) {
      // Close once the complementary card adds to cart; the drawer takes over.
      if (data && data.source === 'product-form' && this.isOpen) this.close();
    }

    onOverlayClick(e) {
      if (e.target === this.DOM.overlay) this.close();
    }

    onEscape(e) {
      if (e.key === 'Escape' && this.isOpen) this.close();
    }

    onTabTrap(e) {
      if (!this.focusTrapActive || e.key !== 'Tab' || !this.DOM) return;
      const focusables = this.getFocusableElements(this.DOM.overlay);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    close() {
      if (!this.DOM || !this.isOpen) return;

      this.isOpen = false;
      this.focusTrapActive = false;

      // Resume the carousel card videos that were playing before we opened.
      (this.pausedVideos || []).forEach((v) => v.play().catch(() => {}));
      this.pausedVideos = undefined;

      this.cartUpdateUnsub?.();
      this.cartUpdateUnsub = undefined;

      this.DOM.overlay.classList.remove('ugc-modal--open');
      this.DOM.overlay.removeEventListener('click', this.onOverlayClick);
      this.DOM.closeBtn.removeEventListener('click', this.close);
      document.removeEventListener('keydown', this.onEscape);
      document.removeEventListener('keydown', this.onTabTrap);

      this.setBackgroundInert(false);

      const opener = this.opener;

      setTimeout(() => {
        this.destroyForms();
        if (this.DOM && this.DOM.overlay && this.DOM.overlay.parentNode) {
          this.DOM.overlay.parentNode.removeChild(this.DOM.overlay);
        }
        document.body.classList.remove('modal-open');
        this.DOM = undefined;
        this.opener = undefined;

        if (opener && typeof opener.focus === 'function') opener.focus();
      }, UgcModal.ANIM);
    }

    getFocusableElements(root) {
      if (!root) return [];
      const selectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'video[controls]',
        'iframe',
        '[tabindex]:not([tabindex="-1"])',
      ];
      return Array.from(root.querySelectorAll(selectors.join(','))).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
    }

    focusFirst() {
      if (!this.DOM) return;
      const target = this.DOM.closeBtn || this.getFocusableElements(this.DOM.overlay)[0];
      if (target && typeof target.focus === 'function') target.focus();
    }

    setBackgroundInert(enable) {
      const supportsInert = 'inert' in HTMLElement.prototype;
      if (enable) {
        this.backgroundNodes = Array.from(document.body.children).filter((node) => node !== this.DOM.overlay);
      }
      (this.backgroundNodes || []).forEach((node) => {
        if (enable) {
          if (supportsInert) node.inert = true;
          else node.setAttribute('aria-hidden', 'true');
        } else if (supportsInert) {
          node.inert = false;
        } else {
          node.removeAttribute('aria-hidden');
        }
      });
      if (!enable) this.backgroundNodes = undefined;
    }
  }

  UgcModal.ANIM = 400;

  function init() {
    if (U.ugcModal) return;
    U.ugcModal = new UgcModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  U.UgcModal = UgcModal;
})();
